/**
 * Activity formatting utilities
 * Used across the application to consistently format activity descriptions
 */

import { getCanonicalActivityType } from './activityType'

/**
 * Format activity description - parse JSON for structured data (insulin, food, etc.)
 * @param activity - The activity object with type and description
 * @param translate - Translation function from react-admin
 * @returns Formatted user-friendly activity description
 */
export const formatActivityDescription = (
  activity: { type?: string; description?: string },
  translate: (key: string) => string,
): string => {
  const canonicalType = getCanonicalActivityType(activity.type)
  const typeFallback = activity.type || translate('pos.common.empty_data')

  if (!activity.description) {
    return typeFallback
  }

  // Try to parse as JSON for structured activities (insulin, food, exercise)
  try {
    const parsed = JSON.parse(activity.description)

    if (typeof parsed === 'object' && parsed !== null) {
      // Handle insulin activities
      if (canonicalType === 'insulin') {
        const { brand, customBrand, dosage, notes } = parsed
        const parts = []

        if (brand && brand !== 'custom') {
          parts.push(brand)
        }
        if (customBrand) {
          parts.push(customBrand)
        }
        if (dosage) {
          parts.push(`${dosage} ${translate('resources.activities.insulin.units')}`)
        }
        if (notes) {
          parts.push(`(${notes})`)
        }

        // A lone note renders as a bare "(We)"; lead with the type label instead.
        if (parts.length === 1 && notes) {
          return `${translate('resources.activities.insulin.label')} — ${notes}`
        }

        return parts.length > 0 ? parts.join(' — ') : typeFallback
      }

      // Handle food activities
      if (canonicalType === 'food') {
        const { foodType, amount, unit } = parsed
        const parts = []

        if (foodType) parts.push(foodType)
        if (amount && unit) parts.push(`${amount} ${unit}`)
        else if (amount) parts.push(amount)

        return parts.length > 0 ? parts.join(' — ') : typeFallback
      }

      // Handle exercise activities
      if (canonicalType === 'exercise') {
        const { exerciseType, duration, intensity } = parsed
        const parts = []

        if (exerciseType) parts.push(exerciseType)
        if (duration) parts.push(duration)
        if (intensity) parts.push(`(${intensity})`)

        return parts.length > 0 ? parts.join(' — ') : typeFallback
      }

      // Generic structured data fallback
      const values = Object.values(parsed).filter((v) => v != null && v !== '')
      if (values.length > 0) {
        return values.join(' — ')
      }
    }
  } catch {
    // JSON parsing failed - description is plain text, not structured JSON
    // This is expected for free-form text descriptions, so no logging needed
  }

  // Default: display as plain text
  return activity.description
}
